import type { DrumVoiceId } from '../data/types';
import { playNoise, playTone } from './synth';

export interface DrumContext {
  out: AudioNode;
  at: number;
  gain: number;
  pan: number | undefined;
  hz: number | undefined;
  release: number | undefined;
  kick: { fromHz: number; toHz: number; release: number };
}

export type DrumVoice = (ctx: DrumContext) => void;

const FRAME_DRUM_HZ = 72;
const DOUM_HZ = 105;

function tom(ctx: DrumContext, hz: number, gain: number, release: number) {
  const { out, at, pan } = ctx;
  playTone(out, at, {
    wave: 'sine',
    hz,
    toHz: hz * 0.6,
    glide: 0.25,
    gain,
    attack: 0.001,
    hold: 0.02,
    release,
    pan,
  });
  playNoise(out, at, {
    gain: gain * 0.25,
    attack: 0.001,
    hold: 0.004,
    release: 0.07,
    pan,
    filter: { type: 'lowpass', hz: 380 },
  });
}

function shine(out: AudioNode, hz: number, q: number, gain: number): AudioNode {
  const filter = out.context.createBiquadFilter();
  filter.type = 'peaking';
  filter.frequency.value = hz;
  filter.Q.value = q;
  filter.gain.value = gain;
  filter.connect(out);
  return filter;
}

export const DRUM_VOICES: Record<DrumVoiceId, DrumVoice> = {
  kick({ out, at, gain, pan, kick }) {
    playTone(out, at, {
      wave: 'sine',
      hz: kick.fromHz,
      toHz: kick.toHz,
      glide: 0.045,
      gain,
      attack: 0.0005,
      hold: 0.04,
      release: kick.release,
      pan,
    });
    playNoise(out, at, {
      gain: 0.14 * gain,
      attack: 0.0005,
      hold: 0.002,
      release: 0.01,
      pan,
      filter: { type: 'highpass', hz: 2500 },
    });
  },
  snare({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.2 * gain,
      attack: 0.001,
      hold: 0.006,
      release: 0.2,
      pan,
      filter: { type: 'bandpass', hz: 1900, q: 0.7 },
    });
    playTone(out, at, {
      wave: 'triangle',
      hz: 215,
      toHz: 170,
      glide: 0.05,
      gain: 0.16 * gain,
      attack: 0.001,
      hold: 0.008,
      release: 0.09,
      pan,
    });
  },
  rim({ out, at, gain, pan }) {
    playTone(out, at, {
      wave: 'triangle',
      hz: 1750,
      gain: 0.07 * gain,
      attack: 0.001,
      hold: 0.003,
      release: 0.03,
      pan,
    });
    playNoise(out, at, {
      gain: 0.07 * gain,
      attack: 0.001,
      hold: 0.002,
      release: 0.02,
      pan,
      filter: { type: 'bandpass', hz: 2600, q: 3 },
    });
  },
  clap({ out, at, gain, pan }) {
    [0, 0.009, 0.018].forEach((offset, index) => {
      playNoise(out, at + offset, {
        gain: 0.14 * gain,
        attack: 0.001,
        hold: 0.002,
        release: index === 2 ? 0.14 : 0.012,
        pan,
        filter: { type: 'bandpass', hz: 1400, q: 1.3 },
      });
    });
  },
  hat({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.11 * gain,
      attack: 0.001,
      hold: 0,
      release: 0.035,
      pan,
      filter: { type: 'highpass', hz: 7000 },
    });
  },
  'hat-open'({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.22 * gain,
      attack: 0.001,
      hold: 0,
      release: 0.12,
      pan,
      filter: { type: 'highpass', hz: 7000 },
    });
  },
  shaker({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.07 * gain,
      attack: 0.008,
      hold: 0.004,
      release: 0.04,
      pan,
      filter: { type: 'highpass', hz: 6500 },
    });
  },
  rattle({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.06 * gain,
      attack: 0.012,
      hold: 0.015,
      release: 0.07,
      pan,
      filter: { type: 'bandpass', hz: 4800, q: 0.8 },
    });
  },
  doum(ctx) {
    tom(ctx, DOUM_HZ, 0.42 * ctx.gain, ctx.release ?? 0.32);
  },
  tek({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.13 * gain,
      attack: 0.001,
      hold: 0.002,
      release: 0.045,
      pan,
      filter: { type: 'bandpass', hz: 3600, q: 1.6 },
    });
    playTone(out, at, {
      wave: 'triangle',
      hz: 1150,
      toHz: 900,
      glide: 0.02,
      gain: 0.04 * gain,
      attack: 0.001,
      hold: 0.002,
      release: 0.03,
      pan,
    });
  },
  'frame-drum'(ctx) {
    tom(ctx, FRAME_DRUM_HZ, 0.55 * ctx.gain, ctx.release ?? 0.45);
  },
  tom(ctx) {
    tom(ctx, ctx.hz ?? FRAME_DRUM_HZ, ctx.gain, ctx.release ?? 0.45);
  },
  udu({ out, at, gain, pan, hz, release }) {
    const pitch = hz ?? 147;
    playTone(out, at, {
      wave: 'sine',
      hz: pitch * 3.2,
      toHz: pitch,
      glide: 0.07,
      gain: 0.5 * gain,
      attack: 0.001,
      hold: 0.03,
      release: release ?? 0.24,
      pan,
    });
    playTone(out, at, {
      wave: 'triangle',
      hz: pitch * 2,
      toHz: pitch * 1.5,
      glide: 0.05,
      gain: 0.12 * gain,
      attack: 0.001,
      hold: 0.01,
      release: 0.09,
      pan,
    });
    playNoise(out, at, {
      gain: 0.1 * gain,
      attack: 0.001,
      hold: 0.003,
      release: 0.035,
      pan,
      filter: { type: 'bandpass', hz: 2200, q: 1.2 },
    });
  },
  cabasa({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.3 * gain,
      attack: 0.006,
      hold: 0.006,
      release: 0.05,
      pan,
      filter: { type: 'bandpass', hz: 7200, q: 0.7 },
    });
  },
  'tuned-kick'({ out, at, gain, pan, hz, kick }) {
    playTone(out, at, {
      wave: 'sine',
      hz: kick.fromHz,
      toHz: hz ?? kick.toHz,
      glide: 0.055,
      gain: 0.85 * gain,
      attack: 0.0008,
      hold: 0.07,
      release: 0.28,
      pan,
    });
    playNoise(out, at, {
      gain: 0.12 * gain,
      attack: 0.0005,
      hold: 0.002,
      release: 0.008,
      pan,
      filter: { type: 'highpass', hz: 4000 },
    });
  },
  'bright-hat'({ out, at, gain, pan }) {
    playNoise(shine(out, 8000, 1.2, 6), at, {
      gain: 0.6 * gain,
      attack: 0.0008,
      hold: 0.002,
      release: 0.04,
      pan,
      filter: { type: 'highpass', hz: 6500 },
    });
  },
  'bright-hat-open'({ out, at, gain, pan }) {
    playNoise(shine(out, 7500, 1, 5), at, {
      gain: 0.5 * gain,
      attack: 0.002,
      hold: 0.01,
      release: 0.18,
      pan,
      filter: { type: 'highpass', hz: 5200 },
    });
  },
  'big-clap'({ out, at, gain, pan }) {
    [0, 0.008, 0.017, 0.026].forEach((offset, index) => {
      const envelope = { attack: 0.001, hold: 0.002, release: index === 3 ? 0.26 : 0.01, pan };
      playNoise(out, at + offset, {
        ...envelope,
        gain: 0.34 * gain,
        filter: { type: 'bandpass', hz: 1900, q: 0.9 },
      });
      playNoise(out, at + offset, {
        ...envelope,
        gain: 0.31 * gain,
        filter: { type: 'highpass', hz: 3200 },
      });
    });
  },
  ride({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.17 * gain,
      attack: 0.001,
      hold: 0.01,
      release: 0.55,
      pan,
      filter: { type: 'bandpass', hz: 9500, q: 0.9 },
    });
  },
  'sand-shaker'({ out, at, gain, pan }) {
    playNoise(out, at, {
      gain: 0.25 * gain,
      attack: 0.012,
      hold: 0.004,
      release: 0.05,
      pan,
      filter: { type: 'bandpass', hz: 6500, q: 1 },
    });
  },
  'wood-rim'({ out, at, gain, pan }) {
    const envelope = { gain: 0.14 * gain, attack: 0.0005, hold: 0.002, release: 0.04, pan };
    playTone(out, at, { ...envelope, wave: 'triangle', hz: 1750 });
    playNoise(out, at, { ...envelope, filter: { type: 'bandpass', hz: 3200, q: 2 } });
  },
};
